import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
  useAddUserFoldersMutation,
  useUpdateUserFoldersMutation
} from '@ports/internal-services/folder/folderSlice';
import { Input } from '../../Input';
import { Button } from '../../Button';
import { showToast } from '../../Toast/Toast';

interface CreateFolderProps {
  isOpen?: boolean;
  onClose?: () => void;
  folderData?: any;
  folderSearchHandler?: () => void;
  module?: string;
}

export const CreateFolder = ({
  isOpen = false,
  onClose,
  folderData,
  folderSearchHandler,
  module
}: CreateFolderProps) => {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState<string>('');

  const [CreateFolder] = useAddUserFoldersMutation();
  const [UpdateFolder] = useUpdateUserFoldersMutation();

  const isEdit = !!folderData;

  useEffect(() => {
    if (folderData) {
      setFolderName(folderData.name || '');
    } else {
      setFolderName('');
    }
  }, [folderData, isOpen]);

  const handleCreate = () => {
    if (folderName) {
      let res;
      if (isEdit) {
        const dataObj = {
          id: folderData.id,
          updateUserFolders: {
            name: folderName,
            module: module ?? undefined
          }
        };
        res = UpdateFolder(dataObj);
      } else {
        const dataObj = {
          createUserFolders: {
            name: folderName,
            module: module ?? undefined
          }
        };
        res = CreateFolder(dataObj);
      }

      res
        .then((res: any) => {
          if (res?.data) {
            // Call folder search handler if provided
            if (folderSearchHandler) {
              folderSearchHandler();
            }

            showToast({
              type: 'success',
              isAlert: true,
              alertProps: {
                type: 'success',
                message: folderData
                  ? t('photosPage.folderUpdateSuccess')
                  : t('photosPage.folderSuccess')
              }
            });

            handleCancel();
          } else {
            const errorMessage =
              res?.error?.data?.message ||
              res?.error?.message ||
              t('photosPage.folderError');

            showToast({
              type: 'error',
              isAlert: true,
              alertProps: {
                type: 'error',
                message: errorMessage
              }
            });
          }
        })
        .catch(err => {
          const errorMessage =
            err?.data?.message || err?.message || t('photosPage.folderError');
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: errorMessage
            }
          });
        });
    }
  };

  const handleCancel = () => {
    setFolderName('');
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[70] confirmation-box-style">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative animate-fadeIn confirmation-box-style">
        <Button
          btnStyle="link"
          className="absolute right-4 top-4"
          onClick={handleCancel}>
          <SVGAssets.CloseIconGray className="w-4 h-4" />
        </Button>

        <h2 className="text-xl font-semibold text-gray-900 pb-3">
          {folderData
            ? t('photosPage.editFolder')
            : t('photosPage.createNewFolder')}
        </h2>

        <hr className="border-gray-200" />

        <div className="mt-6">
          <Input
            name="folderName"
            type="text"
            value={folderName}
            placeholder={t('photosPage.untitled')}
            required
            isWhiteTheme={true}
            onChange={e => setFolderName(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <Button
            btnStyle="link"
            className="px-6 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
            onClick={handleCancel}>
            {t('cancel')}
          </Button>

          <Button
            btnStyle="primary"
            type="button"
            onClick={handleCreate}
            disabled={!folderName.trim()}
            className="rounded-lg px-6 py-2 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            {folderData ? t('update') : t('create')}
          </Button>
        </div>
      </div>
    </div>
  );
};
